import { Box, Button, CircularProgress, Paper, Stack, Tab, Tabs, TextField } from '@mui/material';
import Grid from '@mui/material/Grid';
import { useTranslation } from 'react-i18next';
import { useEffect, useRef, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import { DateRangePicker, DateRange, LocalizationProvider } from '@mui/x-date-pickers-pro';
import PageHeader from '../../common/Components/PageHeader';
import CateringRouteGrid, { CateringRouteGridRef, StatusFilter } from '../CateringRoutes/CateringRouteGrid';
import VipAlertDashboard from './VipAlertDashboard';
import { API } from 'aws-amplify';
import { toast } from "react-toastify";
import { useAuth } from '../../hooks/useAuth';
import DashboardCharts from './DashboardCharts';

function Dashboard() {
    const { t } = useTranslation();
    const [dateRange, setDateRange] = useState<DateRange<Dayjs>>([dayjs(), dayjs().add(20, 'day')]);
    const [tabIndex, setTabIndex] = useState(0);
    const [loading, setLoading] = useState(false);
    const auth = useAuth();
    const [searchText, setSearchText] = useState<string | undefined>(undefined);
    const [filterTerm, setFilterTerm] = useState<string | undefined>(undefined);
    const routeGridRef = useRef<CateringRouteGridRef>(null);

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            setFilterTerm(searchText);
        }, 1000);

        return () => clearTimeout(timeoutId);
    }, [searchText]);

    const handleFilterReset = () => {
        setSearchText('');
        setFilterTerm('');
        routeGridRef.current?.clearFilter();
    };

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabIndex(newValue);
    };

    const handleSync = async () => {
        setLoading(true);

        try {
            let response = await API.post('api', `/api/idps/runsync`, {});

            if (response?.Description) {
                toast.success(response?.Description ?? '');
            } else {
                toast.error('IDPS synchronization could not be started due to an error.');
            }
            setLoading(false);
        } catch (e) {
            console.error(e);
            setLoading(false);
            toast.error('IDPS synchronization could not be started due to an error.');
            return false;
        }
    }

    return (
        <Grid container spacing={3}>
            <Grid item xs={12}>
                <PageHeader title={t('Dashboard')}>
                    <Stack direction='row' spacing={2}>
                        {auth.isSuperAdmin && <Button sx={{ height: 40, width: 120 }} id="sync-button" variant="contained" disableElevation onClick={(e) => handleSync()} disabled={loading}>
                            {loading && <CircularProgress size="25px" />}
                            {!loading && 'IDPS Sync'}
                        </Button>}
                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale='de'>
                            <DateRangePicker value={dateRange} onChange={setDateRange} slotProps={{ textField: { size: 'small' } }} />
                        </LocalizationProvider>
                    </Stack>

                </PageHeader>
            </Grid>
            <DashboardCharts dateFrom={dateRange[0]?.format("YYYY-MM-DD")} dateTo={dateRange[1]?.format("YYYY-MM-DD")} />
            <Grid item xs={12}>
                <Paper sx={{p: 2 }} elevation={3}>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField label="Search" variant="outlined" fullWidth size="small" value={searchText} onChange={(e) => setSearchText(e.target.value)}/>
                        <Button variant="contained" onClick={handleFilterReset} >
                            Reset
                        </Button>
                    </Box>
                </Paper>
            </Grid>
            <Grid item xs={12}>
                <Paper sx={{ p: 0, display: 'flex', flexDirection: 'column' }} elevation={3}>
                    <div style={{ flexBasis: '100%', flexGrow: 1 }} />
                    <Tabs value={tabIndex} onChange={handleTabChange} sx={{ marginLeft: 2 }}>
                        <Tab label="Incomplete Routes" />
                        <Tab label="VIP Alerts" />
                    </Tabs>
                    {tabIndex === 0 && <CateringRouteGrid showIncompleteRoutes={true} dateFrom={dateRange[0]?.format("YYYY-MM-DD")} dateTo={dateRange[1]?.format("YYYY-MM-DD")} statusFilter={StatusFilter.Incompleted} filterTerm={filterTerm} ref={routeGridRef} />}
                    {tabIndex === 1 && <VipAlertDashboard dateFrom={dateRange[0]?.format("YYYY-MM-DD")} dateTo={dateRange[1]?.format("YYYY-MM-DD")} />}
                </Paper>
            </Grid>
        </Grid>
    );
}

export default Dashboard;